import React, { useState, useEffect, useCallback, useRef } from 'react';
import './DateRangeSelector.css';
import {
  detectDateRangePreset,
  formatDateRangeLabel,
  getLocalToday,
  getPresetDateRange,
} from '../utils/localCalendarDate';

interface DateRangeSelectorProps {
  onDateRangeChange: (startDate: string, endDate: string) => void;
  initialStartDate?: string;
  initialEndDate?: string;
  variant?: 'card' | 'header';
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  onDateRangeChange,
  initialStartDate,
  initialEndDate,
  variant = 'card',
}) => {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedPreset, setSelectedPreset] = useState<string>('30');
  const hasInitialized = useRef(false);
  const prevInitialDates = useRef({ start: '', end: '' });
  const isHeader = variant === 'header';

  const presets = isHeader
    ? [
        { value: '7', label: '7 días' },
        { value: '30', label: '30 días' },
        { value: '60', label: '60 días' },
        { value: '90', label: '90 días' },
        { value: 'custom', label: 'Personalizado' },
      ]
    : [
        { value: '7', label: 'Últimos 7 días' },
        { value: '30', label: 'Últimos 30 días' },
        { value: '60', label: 'Últimos 60 días' },
        { value: '90', label: 'Últimos 90 días' },
        { value: 'custom', label: 'Personalizado' },
      ];

  const detectPreset = useCallback((start: string, end: string) => detectDateRangePreset(start, end), []);

  useEffect(() => {
    if (!initialStartDate || !initialEndDate) {
      if (!hasInitialized.current) {
        const defaultDates = getPresetDateRange(30);
        setStartDate(defaultDates.start);
        setEndDate(defaultDates.end);
        setSelectedPreset('30');
        onDateRangeChange(defaultDates.start, defaultDates.end);
        hasInitialized.current = true;
        prevInitialDates.current = { start: defaultDates.start, end: defaultDates.end };
      }
      return;
    }

    const parentDatesChanged =
      prevInitialDates.current.start !== initialStartDate ||
      prevInitialDates.current.end !== initialEndDate;

    if (!hasInitialized.current || parentDatesChanged) {
      setStartDate(initialStartDate);
      setEndDate(initialEndDate);
      setSelectedPreset(detectPreset(initialStartDate, initialEndDate));
      hasInitialized.current = true;
      prevInitialDates.current = { start: initialStartDate, end: initialEndDate };
    }
  }, [initialStartDate, initialEndDate, detectPreset, onDateRangeChange]);

  const handlePresetChange = (preset: string) => {
    setSelectedPreset(preset);

    if (preset === 'custom') {
      return;
    }

    const dates = getPresetDateRange(parseInt(preset, 10));
    setStartDate(dates.start);
    setEndDate(dates.end);
    onDateRangeChange(dates.start, dates.end);
  };

  const handleCustomDateChange = () => {
    if (startDate && endDate && startDate <= endDate) {
      onDateRangeChange(startDate, endDate);
      prevInitialDates.current = { start: startDate, end: endDate };
    }
  };

  const isValidDateRange = startDate && endDate && startDate <= endDate;
  const showCustomInputs = selectedPreset === 'custom';
  const rangeLabel = isValidDateRange ? formatDateRangeLabel(startDate, endDate) : '';

  return (
    <div className={`date-range-selector${isHeader ? ' date-range-selector--header' : ''}`}>
      {!isHeader && (
        <div className="date-range-header">
          <h3>📅 Rango de Fechas</h3>
        </div>
      )}

      <div className="date-range-controls">
        <div className="date-range-toolbar">
          <div className="preset-buttons">
            {presets.map((preset) => (
              <button
                key={preset.value}
                type="button"
                className={[
                  'preset-button',
                  selectedPreset === preset.value ? 'active' : '',
                  preset.value === 'custom' ? 'preset-button--custom' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => handlePresetChange(preset.value)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {showCustomInputs && (
            <div className={`custom-date-inputs${isHeader ? ' custom-date-inputs--header' : ''}`}>
              <div className="date-input-group">
                <label htmlFor="startDate">Desde</label>
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  onBlur={handleCustomDateChange}
                  max={endDate}
                />
              </div>

              <div className="date-input-group">
                <label htmlFor="endDate">Hasta</label>
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  onBlur={handleCustomDateChange}
                  min={startDate}
                  max={getLocalToday()}
                />
              </div>
            </div>
          )}
        </div>

        {rangeLabel && (
          <p className="date-range-label" aria-live="polite">
            {rangeLabel}
          </p>
        )}

        {!isValidDateRange && showCustomInputs && startDate && endDate && (
          <div className="date-error">
            La fecha de inicio debe ser anterior a la fecha de fin
          </div>
        )}
      </div>
    </div>
  );
};

export default DateRangeSelector;
